/**
 * Feeds the Bitrix24 support-chat widget the identity of the logged-in user so
 * the operator sees a customer instead of a guest.
 *
 * Failure is a non-event by design: no retries, no toast, no error boundary.
 * If this hook does nothing, the widget behaves exactly as it did before the
 * feature existed — which is the correct fallback for the one channel people
 * use when something else is already broken.
 *
 * That guarantee has to hold even against a malformed 200 response:
 * `SupportChatIdentity.customData` is Bitrix24's own block format, so the
 * schema deliberately does not pin its inner shape (`supportChatIdentitySchema`
 * only guarantees `customData` is an array of records — see
 * packages/api-types/src/schemas/support-chat.ts). `SupportChatIdentityBridge`
 * mounts this hook directly under the auth provider, above every
 * `<ErrorBoundary>` in `__root.tsx` — a throw here would not degrade to "guest
 * chat", it would white-screen the app. Hence the explicit Array.isArray
 * guards below AND the try/catch around the publish call: either one alone
 * would be enough for the cases we know about, but the GRID shape is
 * externally controlled, so belt and braces.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { escapeBbcode } from '@aica/core';
import type { SupportChatIdentity } from '@aica/api-types';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { publishSupportChatIdentity } from '@/lib/support-chat';

/**
 * Appends a "Page" row to the GRID custom-data block, or returns null when
 * the shape doesn't match what edge-api always sends
 * (`buildSupportChatCustomData`: a USER block plus a GRID array) — a missing
 * GRID block is not itself a fault (the identity is still worth publishing
 * without it), but a GRID key that isn't an array is: that's the shape the
 * unguarded `[...grid]` spread used to crash on, so treat it as invalid and
 * publish nothing rather than guess.
 *
 * `escapeBbcode` (shared with the server-side builder in
 * apps/edge-api/src/routes/support-chat/identity.ts, both from @aica/core)
 * neutralises `[`/`]` in the page URL and title before they enter the
 * `[url=...]` BBCode string — `document.title` is customer-entered data (see
 * routes/projects.$projectId.settings/index.tsx), so building this row
 * without escaping would route straight around the guard the endpoint
 * already applies to every other field.
 *
 * Only origin + pathname go into the row — the query string and hash are
 * stripped. Two reasons: in embedded mode `location.href` carries Bitrix's
 * own `DOMAIN`/`PROTOCOL`/`LANG`/`APP_SID` params, which would leak straight
 * into the support transcript for no benefit (`Surface`, built server-side,
 * already names the Bitrix24 domain); and a query string is exactly the kind
 * of value a customer can shape (e.g. a copied link with tracking params),
 * so dropping it also shrinks what reaches an operator's browser unescaped
 * in spirit even though it is escaped in fact.
 */
function withPageRow(customData: unknown): SupportChatIdentity['customData'] | null {
  if (!Array.isArray(customData)) return null;

  const { origin, pathname } = new URL(window.location.href);
  const pageUrl = `${origin}${pathname}`;
  const pageRow = {
    NAME: 'Page',
    VALUE: `[url=${escapeBbcode(pageUrl)}]${escapeBbcode(
      document.title || pathname
    )}[/url]`,
    DISPLAY: 'LINE',
  };

  let malformedGrid = false;
  const next = customData.map((block) => {
    if (malformedGrid || !block || typeof block !== 'object' || !('GRID' in block)) {
      return block as Record<string, unknown>;
    }
    const grid = (block as Record<string, unknown>).GRID;
    if (!Array.isArray(grid)) {
      malformedGrid = true;
      return block as Record<string, unknown>;
    }
    return { GRID: [...grid, pageRow] };
  });

  return malformedGrid ? null : next;
}

export function useSupportChatIdentity(): void {
  const { apiClient, orgId } = useAuth();

  const { data } = useQuery({
    // orgId is part of the key: switching organization must re-publish, so the
    // operator never sees the previous company next to the current question.
    queryKey: ['support-chat-identity', orgId],
    queryFn: () => apiClient.support.getChatIdentity(),
    // Standalone auth requires X-Selected-Org-ID; without an org the call 400s.
    enabled: Boolean(orgId),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;

    try {
      const customData = withPageRow(data.customData);
      if (!customData) return;

      publishSupportChatIdentity({ ...data, customData });
    } catch {
      // Never let a malformed response escape this effect — see the module
      // doc comment for why that would be far worse than a guest chat.
    }
  }, [data]);
}
